import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import type {
  Carteira,
  InfluenciadorSaldo,
  PaginatedResponse,
  ProductResgatePreview,
  Resgate,
  ResgateAdmin,
  ResgatePreview,
  ResgateToken,
  ResgateTokenComCode,
  ResgateTipo,
} from "@/types/api";

const BASE = "/redemptions";

export const useCarteiras = () =>
  useQuery({
    queryKey: ["carteiras"],
    queryFn: async () => {
      const { data } = await api.get<Carteira[]>(`${BASE}/carteiras`);
      return data;
    },
  });

// Deprecated: soma global de todas as carteiras. Prefira useCarteiras() para saldo por empresa.
export const useSaldo = () =>
  useQuery({
    queryKey: ["redemptions", "saldo"],
    queryFn: async () => {
      const { data } = await api.get<InfluenciadorSaldo>(`${BASE}/saldo`);
      return data;
    },
  });

export const useActiveResgateToken = (carteiraId: string, tipo: ResgateTipo) =>
  useQuery({
    queryKey: ["redemptions", "token", "active", carteiraId, tipo],
    queryFn: async () => {
      const { data } = await api.get<ResgateToken | null>(`${BASE}/tokens/active`, {
        params: { carteira_id: carteiraId, tipo },
      });
      return data;
    },
    enabled: !!carteiraId,
    refetchInterval: 10_000,
  });

export const useGenerateResgateToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { valor: string; tipo: ResgateTipo; carteira_id: string; product_id?: string }) => {
      const { data } = await api.post<ResgateTokenComCode>(`${BASE}/tokens`, payload);
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["carteiras"] });
      qc.invalidateQueries({ queryKey: ["redemptions", "token", "active", vars.carteira_id, vars.tipo] });
    },
  });
};

export const useCancelResgateToken = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: string) => {
      await api.delete(`${BASE}/tokens/${tokenId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["redemptions", "token", "active"] });
      qc.invalidateQueries({ queryKey: ["carteiras"] });
    },
  });
};

export const useResgatePreview = () =>
  useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.get<ResgatePreview>(`${BASE}/preview`, { params: { code } });
      return data;
    },
  });

export const useValidateResgate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.post<Resgate>(`${BASE}/validate`, { code });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["redemptions"] });
    },
  });
};

export const useResgates = (page = 1, pageSize = 10) =>
  useQuery({
    queryKey: ["redemptions", "list", { page, pageSize }],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Resgate>>(`${BASE}/`, {
        params: { page, page_size: pageSize },
      });
      return data;
    },
    placeholderData: (prev) => prev,
  });

export const useCompanyResgates = (page = 1, pageSize = 10) =>
  useQuery({
    queryKey: ["redemptions", "company", { page, pageSize }],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<ResgateAdmin>>(`${BASE}/company`, {
        params: { page, page_size: pageSize },
      });
      return data;
    },
    placeholderData: (prev) => prev,
  });

export const useProductResgatePreview = (productId: string) =>
  useQuery({
    queryKey: ["redemptions", "preview-product", productId],
    queryFn: async () => {
      const { data } = await api.get<ProductResgatePreview>(
        `${BASE}/preview-product/${productId}`,
      );
      return data;
    },
    enabled: !!productId,
    retry: (count, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });
